import {Box3, BufferGeometry, Vector3} from "three";

export type DropAxis = 'x' | 'z';

export const DEFAULT_DROP_THRESHOLD_RATIO = 4;

/**
 * If the "width" of the smallest side of bounding box (among the two axes running parallel to the
 * ground) is at least `thresholdRatio` times as small as the height of the bounding box, we'll
 * return the axis over which to rotate the object into a more stable orientation.
 * @param bounds
 * @param thresholdRatio
 */
function getDropAxis(
  bounds: Box3,
  thresholdRatio = DEFAULT_DROP_THRESHOLD_RATIO,
): DropAxis | null {
  const size = bounds.getSize(new Vector3());
  if (size.z <= size.x && (size.y / size.z) >= thresholdRatio) return 'x';
  if (size.x < size.z && (size.y / size.x) >= thresholdRatio) return 'z';
  return null;
}

/**
 * Adjust geometry for display. The geometry will be cloned, then scaled to fit within a bounding
 * box where each of the dimensions measures `maxLength`. The object will be dropped / "laid down"
 * on its side if the Y side of the bounding box is significantly longer than the X or the Z side.
 */
export function adjustGeometry(
  geometry: BufferGeometry,
  scaleTo: number,
): {
  geometry: BufferGeometry;
  dropAxis: DropAxis | null;
  scalar: number;
  maxSize: number;
} {
  geometry = geometry.clone();
  geometry.computeBoundingBox();
  geometry.center();

  const size    = geometry.boundingBox!.getSize(new Vector3());
  const maxSize = Math.max(size.x, size.y, size.z);

  if (!maxSize) throw new Error('Could not determine geometry size');

  const scalar = scaleTo / maxSize;
  geometry.scale(scalar, scalar, scalar);

  const dropAxis = getDropAxis(geometry.boundingBox!);

  if (dropAxis === "x") geometry.rotateX(Math.PI / 2);
  if (dropAxis === "z") geometry.rotateZ(Math.PI / 2);

  return {
    geometry,
    dropAxis,
    scalar,
    maxSize,
  };
}